import { useState, useEffect, useMemo } from 'react'
import { AppointmentModal } from './AppointmentModal'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

interface Stylist {
  id: string
  name: string
  role: string
  color_hex: string
  active: boolean
}

interface Appointment {
  id: string
  service: string
  date: string
  time: string
  status: string
  duration: number
  notes: string | null
  customer_name: string
  stylist_id: string | null
  stylist_name?: string
  stylist_color?: string
  price?: number
}

type ViewMode = 'day' | 'week'
const MAX_COLUMNS = 10

export function CalendarView() {
  const [currentDate, setCurrentDate] = useState(getToday())
  const [viewMode, setViewMode] = useState<ViewMode>('day')
  const [stylists, setStylists] = useState<Stylist[]>([])
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedStylistIds, setSelectedStylistIds] = useState<Set<string>>(new Set())
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null)

  function getToday(): Date {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }

  function getWeekStart(date: Date): Date {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Monday start
    d.setDate(diff)
    d.setHours(0, 0, 0, 0)
    return d
  }

  // Fetch stylists on mount
  useEffect(() => {
    fetchStylists()
  }, [])

  // Fetch appointments when date/view changes
  useEffect(() => {
    fetchAppointments()
  }, [currentDate, viewMode])

  async function fetchStylists() {
    try {
      const response = await fetch(`${API_URL}/api/stylists?active=1`)
      const data = await response.json()
      const activeStylists = (data.stylists || []).filter((s: Stylist) => s.active)
      setStylists(activeStylists)
      
      // Select all by default (up to MAX_COLUMNS)
      const defaultSelection = new Set<string>(activeStylists.slice(0, MAX_COLUMNS).map((s: Stylist) => s.id))
      setSelectedStylistIds(defaultSelection)
    } catch (err) {
      console.error('Failed to fetch stylists:', err)
    }
  }

  async function fetchAppointments() {
    setLoading(true)
    
    let startDate: string
    let endDate: string
    
    if (viewMode === 'day') {
      startDate = currentDate.toISOString().split('T')[0]
      endDate = startDate
    } else {
      const weekStart = getWeekStart(currentDate)
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekEnd.getDate() + 4) // Mon-Fri
      startDate = weekStart.toISOString().split('T')[0]
      endDate = weekEnd.toISOString().split('T')[0]
    }

    try {
      const response = await fetch(`${API_URL}/api/appointments/range?start=${startDate}&end=${endDate}`)
      const data = await response.json()
      setAppointments(data.appointments || [])
    } catch (err) {
      console.error('Failed to fetch appointments:', err)
    } finally {
      setLoading(false)
    }
  }

  function generateTimeSlots(start: string, end: string, intervalMin: number): string[] {
    const slots: string[] = []
    const [startHour, startMin] = start.split(':').map(Number)
    const [endHour, endMin] = end.split(':').map(Number)

    let currentMin = startHour * 60 + startMin
    const endTotalMin = endHour * 60 + endMin

    while (currentMin < endTotalMin) {
      const hour = Math.floor(currentMin / 60)
      const min = currentMin % 60
      slots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`)
      currentMin += intervalMin
    }

    return slots
  }

  const displayedStylists = useMemo(() => {
    return stylists.filter(s => selectedStylistIds.has(s.id)).slice(0, MAX_COLUMNS)
  }, [stylists, selectedStylistIds])

  const timeSlots = generateTimeSlots('09:00', '18:00', 30)
  const todayStr = new Date().toISOString().split('T')[0]
  const currentDateStr = currentDate.toISOString().split('T')[0]

  function toggleStylist(stylistId: string) {
    setSelectedStylistIds(prev => {
      const next = new Set(prev)
      if (next.has(stylistId)) {
        next.delete(stylistId)
      } else if (next.size < MAX_COLUMNS) {
        next.add(stylistId)
      }
      return next
    })
  }

  function formatDateHeader(date: Date): string {
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    })
  }

  function navigateDate(direction: 'prev' | 'next') {
    const d = new Date(currentDate)
    if (viewMode === 'day') {
      d.setDate(d.getDate() + (direction === 'next' ? 1 : -1))
    } else {
      d.setDate(d.getDate() + (direction === 'next' ? 7 : -7))
    }
    setCurrentDate(d)
  }

  // For week view, generate day columns
  function getWeekDays(): { date: Date; dateStr: string; dayName: string; dayNum: number; isToday: boolean }[] {
    const weekStart = getWeekStart(currentDate)
    const days = []
    for (let i = 0; i < 5; i++) {
      const d = new Date(weekStart)
      d.setDate(d.getDate() + i)
      const dateStr = d.toISOString().split('T')[0]
      days.push({
        date: d,
        dateStr,
        dayName: d.toLocaleDateString('en-GB', { weekday: 'short' }),
        dayNum: d.getDate(),
        isToday: dateStr === todayStr
      })
    }
    return days
  }

  const weekDays = viewMode === 'week' ? getWeekDays() : []

  // Handle appointment click
  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment)
  }

  // Handle appointment save
  const handleAppointmentSave = (updated: Appointment) => {
    setAppointments(prev => prev.map(apt => apt.id === updated.id ? updated : apt))
    setSelectedAppointment(null)
  }

  // Handle appointment cancel
  const handleAppointmentCancel = (appointmentId: string) => {
    setAppointments(prev => prev.map(apt => 
      apt.id === appointmentId ? { ...apt, status: 'cancelled' } : apt
    ))
    setSelectedAppointment(null)
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {viewMode === 'day' ? formatDateHeader(currentDate) : `Week of ${getWeekStart(currentDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
          </h2>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('day')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'day' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Day
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'week' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Week
              </button>
            </div>

            {/* Navigation */}
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentDate(getToday())}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-l-lg hover:bg-gray-50 font-medium"
              >
                Today
              </button>
              <button
                onClick={() => navigateDate('prev')}
                className="px-3 py-1.5 text-sm border-y border-gray-300 hover:bg-gray-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => navigateDate('next')}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-r-lg hover:bg-gray-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Stylist Filter Pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500 font-medium">Stylists:</span>
          {stylists.map(stylist => (
            <button
              key={stylist.id}
              onClick={() => toggleStylist(stylist.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                selectedStylistIds.has(stylist.id)
                  ? 'text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={selectedStylistIds.has(stylist.id) ? { backgroundColor: stylist.color_hex } : {}}
            >
              <span 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: selectedStylistIds.has(stylist.id) ? 'white' : stylist.color_hex }}
              />
              {stylist.name}
            </button>
          ))}
          {selectedStylistIds.size >= MAX_COLUMNS && (
            <span className="text-xs text-amber-600 ml-2">
              (Max {MAX_COLUMNS} columns)
            </span>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 animate-pulse">Loading calendar...</div>
          </div>
        ) : displayedStylists.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            No stylists selected. Select at least one stylist to view the calendar.
          </div>
        ) : viewMode === 'day' ? (
          // DAY VIEW - Multiple stylist columns for single day
          <DayViewGrid
            stylists={displayedStylists}
            timeSlots={timeSlots}
            appointments={appointments}
            dateStr={currentDateStr}
            isToday={currentDateStr === todayStr}
            onAppointmentClick={handleAppointmentClick}
          />
        ) : (
          // WEEK VIEW - Single stylist or merged view
          <WeekViewGrid
            stylists={displayedStylists}
            timeSlots={timeSlots}
            appointments={appointments}
            weekDays={weekDays}
            onAppointmentClick={handleAppointmentClick}
          />
        )}
      </div>

      {/* Legend */}
      <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 text-xs text-gray-600 flex-shrink-0 flex items-center gap-4">
        <span className="font-medium">Status:</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span> Confirmed
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span> Completed
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span> Cancelled
        </span>
        <div className="ml-auto text-gray-500">
          Click any appointment to edit | Showing {displayedStylists.length} of {stylists.length} active stylists
        </div>
      </div>

      {/* Appointment Edit Modal */}
      {selectedAppointment && (
        <AppointmentModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onSave={handleAppointmentSave}
          onCancel={handleAppointmentCancel}
        />
      )}
    </div>
  )
}

// Day View Grid Component
function DayViewGrid({ 
  stylists, 
  timeSlots, 
  appointments,
  dateStr,
  isToday,
  onAppointmentClick
}: { 
  stylists: Stylist[]
  timeSlots: string[]
  appointments: Appointment[]
  dateStr: string
  isToday: boolean
  onAppointmentClick: (apt: Appointment) => void
}) {
  function getSpanningAppointment(time: string, stylistId: string): { apt: Appointment; isStart: boolean } | null {
    const [slotH, slotM] = time.split(':').map(Number)
    const slotMinutes = slotH * 60 + slotM

    for (const apt of appointments) {
      if (apt.stylist_id !== stylistId || apt.date !== dateStr) continue
      
      const [aptH, aptM] = apt.time.split(':').map(Number)
      const aptStart = aptH * 60 + aptM
      const aptEnd = aptStart + (apt.duration || 60)
      
      if (slotMinutes >= aptStart && slotMinutes < aptEnd) {
        return { apt, isStart: slotMinutes === aptStart }
      }
    }
    return null
  }

  return (
    <table className="w-full border-collapse" style={{ minWidth: `${80 + stylists.length * 150}px` }}>
      <thead className="sticky top-0 bg-gray-50 z-10">
        <tr>
          <th className="w-20 border-r border-b border-gray-200 p-2 text-xs font-medium text-gray-600">
            Time
          </th>
          {stylists.map(stylist => (
            <th 
              key={stylist.id} 
              className="border-r border-b border-gray-200 p-2 text-center min-w-[150px]"
              style={{ backgroundColor: `${stylist.color_hex}15` }}
            >
              <div className="flex items-center justify-center gap-2">
                <span 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: stylist.color_hex }}
                />
                <div>
                  <div className="text-sm font-semibold text-gray-900">{stylist.name}</div>
                  <div className="text-xs text-gray-500">{stylist.role}</div>
                </div>
              </div>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {timeSlots.map(time => (
          <tr key={time} className="border-b border-gray-100 h-14">
            <td className="border-r border-gray-200 p-2 text-xs text-gray-500 align-top bg-gray-50 font-mono">
              {time}
            </td>

            {stylists.map(stylist => {
              const spanningInfo = getSpanningAppointment(time, stylist.id)
              
              if (spanningInfo && !spanningInfo.isStart) {
                // This slot is covered by a previous appointment, render continuation
                return (
                  <td 
                    key={stylist.id}
                    className="border-r border-gray-200 p-0"
                    style={{ backgroundColor: `${stylist.color_hex}08` }}
                  >
                    {/* Continuation cell - will be visually connected via CSS */}
                  </td>
                )
              }
              
              return (
                <td
                  key={stylist.id}
                  className={`border-r border-gray-200 p-1 align-top hover:bg-gray-50 ${isToday ? 'bg-blue-50/20' : ''}`}
                >
                  {spanningInfo?.isStart && spanningInfo.apt ? (
                    <AppointmentCard 
                      appointment={spanningInfo.apt} 
                      stylistColor={stylist.color_hex}
                      showDuration
                      onClick={() => onAppointmentClick(spanningInfo.apt)}
                    />
                  ) : null}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// Week View Grid Component
function WeekViewGrid({ 
  stylists, 
  timeSlots, 
  appointments,
  weekDays,
  onAppointmentClick
}: { 
  stylists: Stylist[]
  timeSlots: string[]
  appointments: Appointment[]
  weekDays: { date: Date; dateStr: string; dayName: string; dayNum: number; isToday: boolean }[]
  onAppointmentClick: (apt: Appointment) => void
}) {
  // For week view with multiple stylists, show color-coded appointments
  function getAppointmentsForSlot(time: string, dateStr: string): Appointment[] {
    return appointments.filter(apt => 
      apt.time === time && 
      apt.date === dateStr &&
      stylists.some(s => s.id === apt.stylist_id)
    )
  }

  function getStylistColor(stylistId: string | null): string {
    const stylist = stylists.find(s => s.id === stylistId)
    return stylist?.color_hex || '#6B7280'
  }

  return (
    <table className="w-full border-collapse min-w-[800px]">
      <thead className="sticky top-0 bg-gray-50 z-10">
        <tr>
          <th className="w-16 border-r border-b border-gray-200 p-2 text-xs font-medium text-gray-600">
            Time
          </th>
          {weekDays.map(day => (
            <th 
              key={day.dateStr} 
              className={`border-r border-b border-gray-200 p-2 text-center ${day.isToday ? 'bg-blue-50' : ''}`}
            >
              <div className="text-xs font-medium text-gray-600">{day.dayName}</div>
              <div className={`text-lg font-semibold mt-1 ${day.isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                {day.dayNum}
              </div>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {timeSlots.map(time => (
          <tr key={time} className="border-b border-gray-100">
            <td className="border-r border-gray-200 p-2 text-xs text-gray-500 align-top bg-gray-50 font-mono">
              {time}
            </td>

            {weekDays.map(day => {
              const slotAppointments = getAppointmentsForSlot(time, day.dateStr)
              
              return (
                <td
                  key={day.dateStr}
                  className={`border-r border-gray-200 p-1 align-top hover:bg-gray-50 min-h-[48px] ${day.isToday ? 'bg-blue-50/30' : ''}`}
                >
                  {slotAppointments.length > 0 ? (
                    <div className="space-y-1">
                      {slotAppointments.map(apt => (
                        <AppointmentCard 
                          key={apt.id} 
                          appointment={apt} 
                          stylistColor={getStylistColor(apt.stylist_id)}
                          compact
                          onClick={() => onAppointmentClick(apt)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="h-10"></div>
                  )}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// Appointment Card Component
function AppointmentCard({ 
  appointment, 
  stylistColor,
  compact = false,
  showDuration = false,
  onClick
}: { 
  appointment: Appointment
  stylistColor: string
  compact?: boolean
  showDuration?: boolean
  onClick?: () => void
}) {
  const statusColors = {
    confirmed: { bg: 'bg-white', border: stylistColor, text: 'text-gray-900' },
    completed: { bg: 'bg-green-50', border: '#10B981', text: 'text-green-900' },
    cancelled: { bg: 'bg-red-50', border: '#EF4444', text: 'text-red-400 line-through' }
  }
  
  const status = (appointment.status as keyof typeof statusColors) || 'confirmed'
  const colors = statusColors[status]
  
  return (
    <div 
      onClick={onClick}
      className={`
        ${colors.bg} 
        border-l-4 rounded shadow-sm cursor-pointer hover:shadow-md transition-all hover:scale-[1.02]
        ${compact ? 'p-1.5' : 'p-2'}
      `}
      style={{ borderLeftColor: colors.border === stylistColor ? stylistColor : colors.border }}
    >
      <div className={`font-semibold ${colors.text} ${compact ? 'text-[10px]' : 'text-xs'} truncate`}>
        {appointment.customer_name}
      </div>
      <div className={`text-gray-600 mt-0.5 truncate ${compact ? 'text-[9px]' : 'text-xs'}`}>
        {appointment.service}
      </div>
      {showDuration && appointment.duration && (
        <div className="text-gray-500 mt-0.5 text-[10px]">
          {appointment.duration} min
        </div>
      )}
      {!compact && appointment.stylist_name && (
        <div className="flex items-center gap-1 mt-1">
          <span 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: stylistColor }}
          />
          <span className="text-[10px] text-gray-500 truncate">
            {appointment.stylist_name}
          </span>
        </div>
      )}
    </div>
  )
}
